import { Sparkles, UserRound } from "lucide-react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";
import type { MirrorSurface } from "@/hooks/useMirrorSurface";

interface MirrorSurfaceToggleProps {
  value: MirrorSurface;
  /**
   * Recebe a superfície ALVO. O pai decide o que o toque significa — trocar
   * de verdade ou navegar para o seletor de avatar quando não há avatar
   * (mostrar o GLB placeholder ensinaria a coisa errada).
   */
  onSelect: (next: MirrorSurface) => void;
  className?: string;
}

const ITEM_CLASSES =
  // Rótulo SEMPRE presente ao lado do ícone (cor/ícone nunca é o único
  // portador de significado). transition-transform, não colors: estado
  // selecionado troca de cor instantâneo — nada de animar paint.
  "rounded-full h-7 px-3 gap-1.5 font-display text-[11px] font-semibold " +
  "text-foreground/55 hover:text-foreground/85 hover:bg-white/5 " +
  "data-[state=on]:bg-primary data-[state=on]:text-[#04121a] " +
  "transition-transform active:scale-[0.97]";

/**
 * Toggle avatar × energia do palco do Espelho (decisão Rafa 21/08/2026).
 * Radix ToggleGroup (via ui/toggle-group): radiogroup com setas/foco de
 * teclado de graça. Dumb por desenho — estado e persistência vivem no
 * useMirrorSurface do chamador.
 */
export default function MirrorSurfaceToggle({
  value,
  onSelect,
  className,
}: MirrorSurfaceToggleProps) {
  const { t } = useLanguage();

  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(next) => {
        // Radix devolve "" no toque do item já ativo (deselect) — o palco não
        // tem estado "nenhuma superfície": ignorar.
        if (next === "avatar" || next === "energy") onSelect(next);
      }}
      aria-label={t("mirror.surface.aria")}
      className={cn("rounded-full border p-1 gap-1 backdrop-blur-xl", className)}
      style={{
        backgroundColor: "hsl(222 35% 9% / 0.72)",
        borderColor: "hsl(var(--primary) / 0.25)",
      }}
    >
      <ToggleGroupItem value="avatar" className={ITEM_CLASSES}>
        <UserRound size={13} aria-hidden="true" />
        <span>{t("mirror.surface.avatar")}</span>
      </ToggleGroupItem>
      <ToggleGroupItem value="energy" className={ITEM_CLASSES}>
        <Sparkles size={13} aria-hidden="true" />
        <span>{t("mirror.surface.energy")}</span>
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
